// Team Cup scoring engine.
//
// Ported from SilkTie2026's src/lib/scoring.js. The Team Cup match-points
// model is:
//   • Best-ball NET (or GROSS when handicaps are off) per hole — the
//     team with the lower best-ball wins 1 point. Ties = ½ each.
//   • OFFICIAL scoring: 18 points max (just hole points).
//   • CUSTOM scoring: 18 hole points + 1 bonus point per nine won
//     (tied nine = ½ / ½). Max 20.
//
// Side bets — orthogonal to match-play:
//   • SCATS — lowest GROSS across ALL players in the tournament per
//     hole. Ties cancel (no winner). Dollars only when round is final.
//   • CTH — par-3 closest-to-pin. Per par 3 base pot, unclaimed pots
//     carry forward and split across remaining par 3s.
//
// All functions are pure; no Firebase reads. Callers (admin Team Cup
// tab, scorekeeper, leaderboard, settlement) pass in plain data and
// render whatever comes back.

// Self-contained handicap helpers (ported from SilkTie2026 / GolfGamesScorecard).
// Kept inline rather than extending the project's existing handicap.js so
// the Team Cup module never breaks unrelated formats.

export const DEFAULT_STROKE_INDEX = [
  10, 2, 16, 6, 14, 4, 18, 8, 12,
  5, 1, 13, 3, 15, 7, 17, 9, 11,
]

/** Allocate strokes given to a player per-hole given their Course
 *  Handicap and the course's stroke index. Negative CH (plus-handicaps)
 *  returns negative entries (strokes given back on easiest holes). */
export function allocateStrokes(courseHcp, strokeIndex = DEFAULT_STROKE_INDEX) {
  const out = new Array(18).fill(0)
  const ch = Number(courseHcp)
  if (!Number.isFinite(ch) || ch === 0) return out
  const sign = ch >= 0 ? 1 : -1
  let remaining = Math.abs(ch)
  const order = Array.from({ length: 18 }, (_, i) => ({ hole: i, rank: strokeIndex?.[i] ?? DEFAULT_STROKE_INDEX[i] }))
    .sort((a, b) => a.rank - b.rank)
  const sequence = ch >= 0 ? order : [...order].reverse()
  let layer = 0
  while (remaining > 0) {
    const take = Math.min(18, remaining)
    for (let i = 0; i < take; i++) out[sequence[i].hole] += sign
    remaining -= take
    layer += 1
    if (layer > 4) break
  }
  return out
}

/** Per-hole net = gross − strokes received. */
export function netForHole(gross, strokesReceived) {
  const g = Number(gross)
  if (!Number.isFinite(g) || g <= 0) return null
  return g - (Number(strokesReceived) || 0)
}

// ── Stroke allocation per player ───────────────────────────────────────

/**
 * Build `{ playerId: strokesArray[18] }` from a `{ playerId: courseHandicap }`
 * map and the course's stroke index.
 */
export function strokesForRound(courseHandicaps, strokeIndex = DEFAULT_STROKE_INDEX) {
  const out = {}
  for (const [pid, ch] of Object.entries(courseHandicaps || {})) {
    out[pid] = allocateStrokes(Number(ch) || 0, strokeIndex)
  }
  return out
}

// ── Net helpers ───────────────────────────────────────────────────────

function netForPlayer(scoresByHole, hole, pid, strokesByPlayer) {
  const g = scoresByHole?.[hole]?.[pid]
  if (g == null) return null
  const n = Number(g)
  if (!Number.isFinite(n) || n <= 0) return null
  const sr = strokesByPlayer?.[pid]?.[hole - 1] ?? 0
  return netForHole(n, sr) ?? null
}

function grossForPlayer(scoresByHole, hole, pid) {
  const g = scoresByHole?.[hole]?.[pid]
  if (g == null) return null
  const n = Number(g)
  return Number.isFinite(n) && n > 0 ? n : null
}

/**
 * Lowest net (or gross when handicaps off) on the given hole across the
 * team's player ids. Returns null when no player has a score recorded.
 */
function teamBestBall(team, hole, scoresByHole, strokesByPlayer, handicapsOn) {
  const vals = (team.playerIds || [])
    .map(pid => handicapsOn
      ? netForPlayer(scoresByHole, hole, pid, strokesByPlayer)
      : grossForPlayer(scoresByHole, hole, pid))
    .filter(v => v != null)
  if (vals.length === 0) return null
  return Math.min(...vals)
}

// ── Per-group team match results ───────────────────────────────────────

/**
 * Compute match results for ONE group's 18 holes.
 *
 * Inputs:
 *   teams           — array of team objects: [{ id, name, playerIds: [...] }]
 *                     For best-ball, each team is the SUBSET of players
 *                     in this group. Pass 2+ teams.
 *   scoresByHole    — { [hole]: { [playerId]: grossScore } }
 *   strokesByPlayer — { [playerId]: strokes[18] } (zero-filled when off)
 *   handicapsOn     — bool. When false, best ball uses gross directly.
 *   scoringSystem   — 'official' | 'custom'
 *
 * Returns:
 *   {
 *     teamPoints:   { [teamId]: number }   — final point totals (incl. nine bonus when 'custom')
 *     holePoints:   { [teamId]: number }   — just the hole points
 *     ninePoints:   { [teamId]: { front, back } }  — nine bonus contributions (0 when 'official')
 *     holes:        [{ winnerId | 'tie' | null, bests: { [teamId]: number|null } }] length 18
 *     valid:        bool (at least 2 teams with player ids)
 *   }
 */
export function computeTeamMatchResults({
  teams, scoresByHole, strokesByPlayer, handicapsOn = false, scoringSystem = 'official',
}) {
  const ts = Array.isArray(teams) ? teams.filter(t => (t.playerIds || []).length > 0) : []
  const emptyHoles = Array.from({ length: 18 }, () => ({ winnerId: null, bests: {} }))
  const teamPoints = {}
  const holePoints = {}
  const ninePoints = {}
  ts.forEach(t => { teamPoints[t.id] = 0; holePoints[t.id] = 0; ninePoints[t.id] = { front: 0, back: 0 } })
  if (ts.length < 2) {
    return { teamPoints, holePoints, ninePoints, holes: emptyHoles, valid: false }
  }

  const holes = []
  for (let i = 0; i < 18; i++) {
    const hole = i + 1
    const bests = {}
    ts.forEach(t => {
      bests[t.id] = teamBestBall(t, hole, scoresByHole, strokesByPlayer, handicapsOn)
    })
    const definedVals = Object.values(bests).filter(v => v != null)
    // Hole is decided once every team in this group has at least one
    // entered score. (Partial holes — e.g. only some teams entered —
    // hold the points until all teams report.)
    const allReported = definedVals.length === ts.length
    if (!allReported) {
      holes.push({ winnerId: null, bests })
      continue
    }
    const min = Math.min(...definedVals)
    const winners = ts.filter(t => bests[t.id] === min)
    if (winners.length === 1) {
      const w = winners[0].id
      holes.push({ winnerId: w, bests })
      holePoints[w] += 1
    } else {
      // Halved hole among the tied teams. Each gets ½ point per tied
      // pair... simplified to "split 1 point equally among winners".
      holes.push({ winnerId: 'tie', bests })
      const share = 1 / winners.length
      winners.forEach(t => { holePoints[t.id] += share })
    }
  }

  // ── Nine bonus (custom scoring only) ─────────────────────────────────
  if (scoringSystem === 'custom') {
    const wins = (start, end) => {
      const ws = {}
      ts.forEach(t => { ws[t.id] = 0 })
      let allDone = true
      for (let h = start; h <= end; h++) {
        const { winnerId } = holes[h]
        if (winnerId == null) { allDone = false; break }
        if (winnerId === 'tie') {
          const min = Math.min(...Object.values(holes[h].bests))
          const tied = ts.filter(t => holes[h].bests[t.id] === min)
          tied.forEach(t => { ws[t.id] += 1 / tied.length })
        } else {
          ws[winnerId] += 1
        }
      }
      return allDone ? ws : null
    }
    const front = wins(0, 8)
    const back  = wins(9, 17)
    const award = (wsMap, key) => {
      if (!wsMap) return
      const max = Math.max(...Object.values(wsMap))
      const leaders = Object.entries(wsMap).filter(([, v]) => v === max).map(([k]) => k)
      const share = 1 / leaders.length
      leaders.forEach(id => { ninePoints[id][key] += share })
    }
    award(front, 'front')
    award(back, 'back')
  }

  // Total = hole points + nine bonuses.
  ts.forEach(t => {
    teamPoints[t.id] = holePoints[t.id] + ninePoints[t.id].front + ninePoints[t.id].back
  })

  return { teamPoints, holePoints, ninePoints, holes, valid: true }
}

// ── Scats — lowest gross across ALL players ───────────────────────────

/**
 * Compute Scats winners and dollars across the whole tournament.
 *
 * Inputs:
 *   playerIds       — flat list of every player participating
 *   scoresByGroup   — { [groupCode]: { [hole]: { [pid]: gross } } }
 *   perHole         — base pot per hole (number)
 *   roundFinal      — only emit dollars when true. Hole counts always emit.
 *
 * Returns:
 *   {
 *     holesWonByPlayer: { [pid]: count }
 *     payoutsByPlayer:  { [pid]: dollars }   — winner net = (n-1) * perHole;
 *                                              each loser net = -perHole.
 *                                              Empty when !roundFinal.
 *     perHole: [{ hole, winnerId | 'tie' | null, gross }]  length 18
 *   }
 *
 * Spec: "low gross outright across all players wins each hole; ties =
 * no winner". Dollars: winner is paid `perHole` by every other player
 * for each hole they won.
 */
export function computeScatPayouts({ playerIds, scoresByGroup, perHole = 0, roundFinal = false }) {
  const holesWonByPlayer = {}
  const payoutsByPlayer = {}
  const allIds = Array.isArray(playerIds) ? playerIds : []
  allIds.forEach(pid => { holesWonByPlayer[pid] = 0; payoutsByPlayer[pid] = 0 })
  const perHoleRows = []

  for (let h = 1; h <= 18; h++) {
    // Pull every player's gross for this hole.
    const entries = []
    for (const pid of allIds) {
      let v = null
      for (const grpScores of Object.values(scoresByGroup || {})) {
        const g = grpScores?.[h]?.[pid]
        if (g != null) { v = Number(g); break }
      }
      if (Number.isFinite(v) && v > 0) entries.push({ pid, gross: v })
    }
    if (entries.length < allIds.length) {
      // Hole not fully scored yet.
      perHoleRows.push({ hole: h, winnerId: null, gross: null })
      continue
    }
    const min = Math.min(...entries.map(e => e.gross))
    const winners = entries.filter(e => e.gross === min)
    if (winners.length !== 1) {
      perHoleRows.push({ hole: h, winnerId: 'tie', gross: min })
      continue
    }
    const w = winners[0].pid
    holesWonByPlayer[w] = (holesWonByPlayer[w] || 0) + 1
    perHoleRows.push({ hole: h, winnerId: w, gross: min })
  }

  if (roundFinal && perHole > 0) {
    // Net payout per player: wins × perHole × (n−1) earned − losses × perHole paid.
    // "Decided holes" = holes won by exactly one player (ties are excluded).
    const decidedHoles = perHoleRows.filter(r => r.winnerId && r.winnerId !== 'tie').length
    allIds.forEach(pid => {
      const won = holesWonByPlayer[pid] || 0
      const losses = decidedHoles - won
      payoutsByPlayer[pid] = (won * perHole * (allIds.length - 1)) - (losses * perHole)
    })
  }

  return { holesWonByPlayer, payoutsByPlayer, perHole: perHoleRows }
}

// ── CTH — per-par-3 closest-to-pin with carryover ─────────────────────

/**
 * Compute CTH payouts. Same shape as SilkTie2026's computeCthValues,
 * adapted for a single round with N groups.
 *
 * Inputs:
 *   pars              — array of 18 par values (3/4/5)
 *   cthByHole         — { [hole]: { [groupCode]: { v: playerId | 'none', t: ts } } }
 *                       OR just  { [hole]: playerId | 'none' } when a single
 *                       group; both forms are accepted.
 *   groupCodes        — array of group codes participating (used to know
 *                       when a hole is "fully reported")
 *   perPar3           — base pot per par 3
 *
 * Returns:
 *   {
 *     perPlayer: { [pid]: dollars }
 *     perHole: [{ hole, par, winnerId | 'none' | null, paid, carryFromHere }]
 *     leftoverBucket: dollars unclaimed at end
 *   }
 *
 * Carryover model: pots are accumulated over par-3 holes. When a par-3
 * resolves "all groups submitted None", that pot rolls into the bucket
 * and is split equally across REMAINING par-3 pots in this round. If
 * no par-3s remain, the bucket stays unclaimed.
 */
export function computeCthValues({ pars, cthByHole, groupCodes = [], perPar3 = 0 }) {
  const par3Holes = []
  for (let i = 0; i < 18; i++) if (pars?.[i] === 3) par3Holes.push(i + 1)
  const pots = par3Holes.map(() => perPar3)
  const perPlayer = {}
  const perHole = []
  let leftoverBucket = 0
  const bump = (pid, amt) => { perPlayer[pid] = (perPlayer[pid] || 0) + amt }
  const expected = Math.max(1, groupCodes.length)

  for (let i = 0; i < par3Holes.length; i++) {
    const hole = par3Holes[i]
    const subs = cthByHole?.[hole]
    const potThis = pots[i]
    let winnerId = null
    if (subs == null) {
      perHole.push({ hole, par: 3, winnerId: null, paid: 0, carryFromHere: 0 })
      continue
    }
    // Normalize submissions list.
    let entries = []
    if (typeof subs === 'string') {
      entries = [{ v: subs, t: 0 }]
    } else if (typeof subs === 'object') {
      for (const raw of Object.values(subs)) {
        if (typeof raw === 'string') entries.push({ v: raw, t: 0 })
        else if (raw && typeof raw.v === 'string') entries.push({ v: raw.v, t: Number(raw.t) || 0 })
      }
    }
    if (entries.length < expected) {
      perHole.push({ hole, par: 3, winnerId: null, paid: 0, carryFromHere: 0 })
      continue
    }
    const real = entries.filter(e => e.v !== 'none')
    if (real.length === 0) {
      // All None — carry to remaining par-3 pots.
      winnerId = 'none'
      const remaining = par3Holes.length - 1 - i
      if (remaining > 0) {
        const each = potThis / remaining
        for (let j = i + 1; j < par3Holes.length; j++) pots[j] += each
      } else {
        leftoverBucket += potThis
      }
      perHole.push({ hole, par: 3, winnerId, paid: 0, carryFromHere: potThis })
      continue
    }
    // Latest non-None submission wins.
    real.sort((a, b) => b.t - a.t)
    winnerId = real[0].v
    bump(winnerId, potThis)
    perHole.push({ hole, par: 3, winnerId, paid: potThis, carryFromHere: 0 })
  }

  return { perPlayer, perHole, leftoverBucket }
}

// ── Hole status helpers ───────────────────────────────────────────────

/**
 * For a single group: count entered and decided holes — used to derive
 * "live" vs "final" status. A hole is decided when every team in the
 * group has a best ball.
 */
export function computeHoleStatus(teams, scoresByHole, strokesByPlayer, handicapsOn) {
  let decided = 0
  for (let i = 0; i < 18; i++) {
    const hole = i + 1
    const allReported = (teams || []).every(t => {
      const v = teamBestBall(t, hole, scoresByHole, strokesByPlayer, handicapsOn)
      return v != null
    })
    if (allReported) decided += 1
  }
  return { decided, total: 18, complete: decided >= 18 }
}

export { netForPlayer, grossForPlayer, teamBestBall }
